import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ShieldCheck, CheckCircle, ArrowRight, Sparkle, ChartLineUp, Lock,
  ArrowUpRight, Files, ClipboardText, Storefront, FlowArrow,
} from '@phosphor-icons/react';
import { HaleWordmark, HALE_RED } from '../components/HaleBrand';
import TI from '../components/brand/TI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Approved ToxIntel palette — re-used here so /hale visually nests
// inside the ToxIntel design system without redesigning anything else.
const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* ====================================================================
 * /hale — Hale × ToxIntel partnership landing
 *
 * Visual treatment matches the approved ToxIntel landing system:
 *   - ivory cream background, slate body, coral accent
 *   - Cormorant Garamond serif headlines, Inter body
 *   - typography-led hero (no photo)
 *   - icon-driven feature cards
 *   - workflow diagram instead of marketing imagery
 *   - pricing table sourced from /api/hale-marketplace/pricing
 *
 * The Hale brand block (HaleWordmark — red rectangle, Archivo Black)
 * remains identity-locked since it carries Hale's own brand equity.
 * Everything around it is ToxIntel-system styling.
 * ==================================================================== */

const PILLARS = [
  { icon: ShieldCheck,  title: 'Proactive scientific governance',
    desc: 'Ingredient transparency, exposure-aware review, and structured workflows — built so modern brands stay ahead of evolving retailer and documentation expectations.' },
  { icon: Lock,         title: 'Operational maturity, built-in',
    desc: 'Reusable governance infrastructure: documentation portability, supplier-supported records, audit trails, and reviewer-led escalation pathways.' },
  { icon: ChartLineUp,  title: 'Retailer-oriented interoperability',
    desc: 'Structured onboarding aligned to the documentation expectations modern retailers are converging on. Built for portability, not one-off audits.' },
  { icon: Sparkle,      title: 'Premium, founder-friendly',
    desc: 'Sophisticated SaaS activation — purpose-built for grooming, fragrance, wellness, body care, and lifestyle brands.' },
];

const WHY_FOR_BRANDS = [
  { kicker: '01', title: 'Retailer documentation, before retailers ask.',
    body: 'Sephora, Credo, Whole Foods, Target and Ulta are converging on structured ingredient transparency and exposure-aware safety documentation. Hale puts that documentation in your hands now — portable across retailers, not locked to any single audit.' },
  { kicker: '02', title: 'Scientific review without staffing a tox team.',
    body: 'Independent toxicologists run the ingredient and exposure review through ToxIntel. Your team gets retailer-ready memos, badges, and audit trails without hiring a regulatory department.' },
  { kicker: '03', title: 'Operational documentation that compounds.',
    body: 'Every SKU you put through Hale builds a reusable governance record — supplier docs, packaging stewardship, claims substantiation — that you re-use for the next launch, the next jurisdiction, and the next retailer.' },
];

const WORKFLOW_NODES = [
  { code: 'HX',  label: 'Hale',     sub: 'Brand activation layer' },
  { code: 'TX',  label: 'ToxIntel', sub: 'Scientific review · assessment infrastructure' },
  { code: 'RT',  label: 'Retailer', sub: 'Documentation handoff' },
];

const REVIEWED_FEATURES = [
  'Ingredient transparency review',
  'Formula governance (leave-on / rinse-off)',
  'Packaging governance overlays',
  'Documentation portability',
  'Reviewer-led escalation pathway',
];

const VERIFIED_FEATURES = [
  'Supplier-supported documentation',
  'Packaging stewardship records',
  'Ingredient transparency workflows',
  'Brand governance maturity',
];

export default function HaleLandingPage() {
  const [catalog, setCatalog] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/hale/catalog`).then(r => setCatalog(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/hale-marketplace/exhibit-a/preview`).then(r => setPreview(r.data || null)).catch(() => {});
  }, []);

  // Group preview services by category for the public teaser
  const previewByCategory = (preview?.services || []).reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Hale × ToxIntel · Governance Infrastructure for Modern Brands</title>
        <meta name="description" content="Hale by ToxIntel — proactive scientific governance, retailer-ready documentation, and reusable compliance infrastructure for beauty, wellness, and lifestyle brands. Powered by ToxIntel scientific review and governance infrastructure." />
      </Helmet>

      {/* ============================================================
          HEADER — matches ToxIntel landing brand lockup
          ============================================================ */}
      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="hale-brand-mark">
            <Link
              to="/"
              className="flex items-center gap-3"
              data-testid="hale-brand-mark-toxintel"
              aria-label="ToxIntel home"
            >
              <span
                className="text-xl sm:text-2xl tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
              >
                <TI color={CORAL} />
              </span>
            </Link>
            <span
              className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] hidden md:inline px-2 py-1 rounded-sm"
              style={{ backgroundColor: SLATE, color: IVORY }}
              data-testid="hale-section-chip"
            >
              · Hale partnership
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <a href="#why" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-nav-why">Why Hale</a>
            <a href="#pathways" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-nav-pathways">Pathways</a>
            <a href="#pricing" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-nav-pricing">Exhibit A</a>
            <Link to="/login" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: SLATE }} data-testid="hale-nav-signin">
              Brand sign in &rarr;
            </Link>
          </nav>
        </div>
      </header>

      <main>
        {/* ==========================================================
            HERO — typography-led, no imagery
            ========================================================== */}
        <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="mb-6">
                <HaleWordmark size="lg" />
              </div>
              <p
                className="text-[10px] font-mono uppercase tracking-[0.35em] mb-4"
                style={{ color: CORAL }}
                data-testid="hale-hero-eyebrow"
              >
                Hale × ToxIntel · Governance infrastructure
              </p>
              <h1
                className="font-extrabold tracking-tight leading-[1.02] text-[clamp(2.5rem,5vw,4.25rem)] mb-6"
                style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif', color: SLATE }}
                data-testid="hale-hero-title"
              >
                Modern governance infrastructure for{' '}
                <em style={{ color: CORAL, fontStyle: 'italic', fontWeight: 600 }}>beauty, wellness, and lifestyle brands.</em>
              </h1>
              <p className="text-base sm:text-lg leading-relaxed max-w-2xl mb-10" style={{ color: MUTED }}>
                Proactive scientific governance — ingredient transparency, packaging governance overlays,
                supplier-supported documentation, and reusable retailer-readiness infrastructure.
                Powered by ToxIntel scientific review and governance infrastructure.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/hale/marketplace"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm"
                  style={{ backgroundColor: CORAL, color: SLATE }}
                  data-testid="hale-cta-marketplace"
                >
                  Explore membership pathways <ArrowUpRight size={14} weight="bold" />
                </Link>
                <Link
                  to="/hale/apply"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border"
                  style={{ borderColor: SLATE, color: SLATE }}
                  data-testid="hale-cta-apply"
                >
                  Begin application
                </Link>
                <Link
                  to="/hale/dashboard"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider"
                  style={{ color: MUTED }}
                  data-testid="hale-cta-dashboard"
                >
                  Brand dashboard &rarr;
                </Link>
              </div>
            </div>

            {/* Right rail — scientific governance card (matches ToxIntel right rail) */}
            <aside className="lg:col-span-1">
              <div className="rounded-sm border p-7 h-full" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-5" style={{ color: CORAL }}>
                  Hale Pilot · Phase 1
                </p>
                <ul className="space-y-4 text-sm leading-snug">
                  <li className="flex gap-3">
                    <CheckCircle size={16} weight="fill" style={{ color: CORAL }} className="mt-0.5 flex-shrink-0" />
                    <span>Independent scientific review signed by ToxIntel toxicologists.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle size={16} weight="fill" style={{ color: CORAL }} className="mt-0.5 flex-shrink-0" />
                    <span>Aligned to MoCRA, EU 1223, Prop 65, IFRA, and PFAS multi-jurisdiction frameworks.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle size={16} weight="fill" style={{ color: CORAL }} className="mt-0.5 flex-shrink-0" />
                    <span>Documentation portability — re-usable across Sephora, Credo, Whole Foods, Target, Ulta.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle size={16} weight="fill" style={{ color: CORAL }} className="mt-0.5 flex-shrink-0" />
                    <span>Exposure-based, not hazard-only scoring — defensible governance tone.</span>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* ==========================================================
            WHY THIS MATTERS FOR BRANDS
            ========================================================== */}
        <section id="why" className="border-t" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Why Hale matters for brands
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-12 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              Retailer expectations are converging. Hale puts you ahead of them.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {WHY_FOR_BRANDS.map(item => (
                <div
                  key={item.kicker}
                  className="rounded-sm border p-7"
                  style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
                  data-testid={`hale-why-${item.kicker}`}
                >
                  <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                    {item.kicker}
                  </p>
                  <h3
                    className="text-xl font-extrabold tracking-tight mb-3"
                    style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: MUTED }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ==========================================================
            TWO PATHWAYS — Reviewed (coral primary) + Verified (slate secondary)
            ========================================================== */}
        <section id="pathways" className="border-t" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Two governance pathways
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-10 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              Hale Reviewed™ and Hale Verified™ — complementary, not exclusive.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reviewed — primary */}
              <div
                className="rounded-sm border-2 p-8 relative"
                style={{ borderColor: CORAL, backgroundColor: IVORY }}
                data-testid="hale-pathway-reviewed"
              >
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
                  Reviewed™
                </p>
                <h3
                  className="text-2xl font-extrabold tracking-tight mb-3"
                  style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                >
                  Hale Reviewed™
                </h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: MUTED }}>
                  Scientific governance activation. Structured ingredient review, packaging governance overlays,
                  documentation interoperability, and reviewer-led escalation pathways — assembled into
                  reusable retailer-readiness infrastructure.
                </p>
                <ul className="space-y-2 mb-2">
                  {REVIEWED_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2 text-[13px]" style={{ color: SLATE }}>
                      <CheckCircle size={14} weight="fill" style={{ color: CORAL }} className="mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Verified — secondary */}
              <div
                className="rounded-sm border p-8"
                style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
                data-testid="hale-pathway-verified"
              >
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: MUTED }}>
                  Verified™
                </p>
                <h3
                  className="text-2xl font-extrabold tracking-tight mb-3"
                  style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                >
                  Hale Verified™
                </h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: MUTED }}>
                  Documentation maturity for sustainability and supplier interoperability.
                  Supplier-supported records, packaging stewardship documentation, transparency
                  workflows, and brand-governance readiness — a parallel pathway for evolving
                  retailer documentation expectations.
                </p>
                <ul className="space-y-2 mb-3">
                  {VERIFIED_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2 text-[13px]" style={{ color: SLATE }}>
                      <CheckCircle size={14} weight="fill" style={{ color: MUTED }} className="mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
                  Workflow available · onboarding through Phase H2.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ==========================================================
            WORKFLOW DIAGRAM — Hale → ToxIntel → Retailer
            ========================================================== */}
        <section className="border-t" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              The Hale workflow
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-12 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              How Hale, ToxIntel, and retailers connect.
            </h2>
            <div className="flex flex-col md:flex-row items-stretch gap-3" data-testid="hale-workflow-diagram">
              {WORKFLOW_NODES.map((node, idx) => (
                <React.Fragment key={node.code}>
                  <div
                    className="flex-1 rounded-sm border p-6 flex flex-col items-start"
                    style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.7)' }}
                    data-testid={`hale-workflow-node-${node.code.toLowerCase()}`}
                  >
                    <span
                      className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3 px-2 py-1 rounded-sm"
                      style={{ backgroundColor: SLATE, color: IVORY }}
                    >
                      {node.code}
                    </span>
                    <p
                      className="text-2xl font-extrabold tracking-tight mb-1"
                      style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                    >
                      {node.label}
                    </p>
                    <p className="text-[12px]" style={{ color: MUTED }}>{node.sub}</p>
                  </div>
                  {idx < WORKFLOW_NODES.length - 1 && (
                    <div className="flex items-center justify-center md:px-1" aria-hidden>
                      <FlowArrow size={20} weight="bold" style={{ color: CORAL, transform: 'rotate(0deg)' }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="mt-6 text-[12px] italic max-w-3xl" style={{ color: MUTED }}>
              Hale is the brand-activation layer that packages governance into pathways, badges, and
              documentation. ToxIntel is the scientific review and assessment infrastructure beneath. The output is
              portable documentation that retailers can consume directly.
            </p>
          </div>
        </section>

        {/* ==========================================================
            MARKETPLACE PREVIEW (gated) — public teaser only.
            Detailed rates and "includes" content live inside the
            authenticated /hale/marketplace surface.
            ========================================================== */}
        <section id="pricing" className="border-t" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Hale Marketplace · gated catalog
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              A curated services marketplace for approved Hale League Brands.
            </h2>
            <p className="text-sm mb-8 max-w-2xl" style={{ color: MUTED }}>
              Detailed rates, service scope, and the signed Exhibit A live inside the gated Hale
              Marketplace — visible only to approved brands and Hale / ToxIntel admins.
              The categories below show what&apos;s available; sign in or apply for access to see the full catalog.
            </p>

            {/* Category preview cards */}
            {preview?.categories && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="hale-marketplace-preview-grid">
                {preview.categories.filter(c => c.id !== 'database_access').map(cat => {
                  const services = previewByCategory[cat.id] || [];
                  return (
                    <div
                      key={cat.id}
                      className="rounded-sm border p-5"
                      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
                      data-testid={`hale-marketplace-preview-${cat.id}`}
                    >
                      <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
                        {String(services.length).padStart(2, '0')} services
                      </p>
                      <p className="text-base font-semibold mb-2" style={{ color: SLATE }}>{cat.label}</p>
                      <p className="text-[12px] leading-relaxed mb-3" style={{ color: MUTED }}>{cat.desc}</p>
                      <ul className="text-[11px] space-y-1" style={{ color: MUTED }}>
                        {services.slice(0, 3).map(s => (
                          <li key={s.id} className="truncate">· {s.name}</li>
                        ))}
                        {services.length > 3 && (
                          <li className="italic" style={{ color: CORAL }}>+ {services.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-2">
              <Link
                to="/hale/marketplace"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm"
                style={{ backgroundColor: CORAL, color: SLATE }}
                data-testid="hale-pricing-cta-marketplace"
              >
                Open Hale Marketplace <ArrowUpRight size={14} weight="bold" />
              </Link>
              <Link
                to="/hale/apply"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border"
                style={{ borderColor: SLATE, color: SLATE }}
                data-testid="hale-pricing-cta-apply"
              >
                Apply for access
              </Link>
              <a
                href="mailto:partnerships@tagc.us?subject=Hale%20Marketplace%20%E2%80%94%20Access%20Request"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider"
                style={{ color: MUTED }}
                data-testid="hale-pricing-cta-contact"
              >
                Contact partnerships &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* ==========================================================
            PILLARS
            ========================================================== */}
        <section className="border-t" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Built for operational maturity
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
              {PILLARS.map(p => (
                <div
                  key={p.title}
                  className="rounded-sm border p-6"
                  style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
                  data-testid={`hale-pillar-${p.title.toLowerCase().split(' ').slice(0,2).join('-')}`}
                >
                  <p.icon size={22} weight="duotone" style={{ color: CORAL }} className="mb-3" />
                  <p className="text-sm font-semibold mb-2" style={{ color: SLATE }}>{p.title}</p>
                  <p className="text-[12px] leading-relaxed" style={{ color: MUTED }}>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ==========================================================
            VERDICT LANGUAGE
            ========================================================== */}
        <section className="border-t" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}>
          <div className="max-w-7xl mx-auto px-6 py-20">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Governance-tone verdicts · enterprise voice
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-10 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              Defensible language. Retailer-ready. Never consumer fear.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
              {(catalog?.verdicts || [
                { id: 'no_concern',           label: 'No significant concern identified' },
                { id: 'monitor',              label: 'Monitor' },
                { id: 'further_review',       label: 'Further review recommended' },
                { id: 'high_priority_concern', label: 'Escalation Review Recommended' },
              ]).map(v => (
                <div
                  key={v.id}
                  className="rounded-sm border p-4 text-center"
                  style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
                  data-testid={`hale-verdict-${v.id}`}
                >
                  <p className="text-[12px] font-medium" style={{ color: SLATE }}>{v.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ==========================================================
        {/* FOOTER — ToxIntel-led; Aᴳ removed from public Hale surface */}
        <footer className="border-t" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <span
                className="text-base tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}
              >
                <TI color={CORAL} />
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
                × Hale
              </span>
            </div>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
              ToxIntel · Governance infrastructure for modern brands
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
